import { Link } from 'react-router-dom'
import { useState, useEffect, useRef, useMemo } from 'react';
import { usePagination } from '../../contexts/PaginationContext';
import { useNotification } from '../../contexts/NotificationContext';
import PaginationControls from '../../components/PaginationControls';
import TriagensTable from '../../components/TriagensTable';
import Footer from '../../components/Footer';
import { authFetch } from '../../utils/authFetch';
import DateInput from '../../components/DateInput';
import { Modal } from 'react-bootstrap';
import 'bootstrap-icons/font/bootstrap-icons.css';

const getDefaultPeriod = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0]
  };
};

const Triagens = () => {
  const defaultPeriod = useMemo(getDefaultPeriod, []);
  const [triagens, setTriagens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [showModalExportar, setShowModalExportar] = useState(false);
  const [dataInicio, setDataInicio] = useState(defaultPeriod.startDate);
  const [dataFim, setDataFim] = useState(defaultPeriod.endDate);
  const [exporting, setExporting] = useState(false);
  const isFirstRequest = useRef(true);

  const { showNotification } = useNotification();

  const {
    page,
    pageSize,
    setTotalElements,
    setTotalPages,
    setPage,
  } = usePagination();

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (value.length >= 3 || value.length === 0) {
      const timeoutId = setTimeout(() => {
        isFirstRequest.current = true;
        fetchTriagens(value);
      }, 500);
      setSearchTimeout(timeoutId);
    }
  };

  const fetchTriagens = async (patientName = searchTerm) => {
    try {
      setLoading(true);
      const currentPage = isFirstRequest.current ? 0 : page;
      const queryParams = new URLSearchParams();
      if (patientName && patientName.length >= 3) {
        queryParams.append('patientName', patientName);
      }
      
      const url = `screening/${currentPage}/${pageSize}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await authFetch(url);

      if (!response.ok) throw new Error("Erro ao buscar triagens");

      const data = await response.json();
      setTriagens(data.content || []);
      setTotalElements(data.totalElements || 0);
      setTotalPages(data.totalPages || 0);
      
      setPage(currentPage);
      isFirstRequest.current = false;
    } catch (err) {
      setError(err.message);
      showNotification("danger", "Erro ao carregar triagens");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTriagens();
  }, [page, pageSize]);

  const handleOpenModalExportar = () => {
    const { startDate, endDate } = getDefaultPeriod();
    setDataInicio(startDate);
    setDataFim(endDate);
    setShowModalExportar(true);
  };

  const handleExportar = async (e) => {
    e.preventDefault();
    if (!dataInicio || !dataFim) {
      showNotification('danger', 'Informe a data início e a data fim.');
      return;
    }
    if (new Date(dataInicio) > new Date(dataFim)) {
      showNotification('danger', 'Data início não pode ser maior que a data fim.');
      return;
    }
    try {
      setExporting(true);
      // Endpoint de exportação de triagens por período (altere conforme o endpoint informado)
      const url = `screening/export?dataInicio=${dataInicio}&dataFim=${dataFim}`;
      const response = await authFetch(url, {
        method: 'GET',
        headers: { Accept: 'text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
      });
      if (!response.ok) throw new Error('Erro ao exportar.');
      const blob = await response.blob();
      const urlBlob = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = urlBlob;
      link.download = `triagens_${dataInicio}_${dataFim}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(urlBlob);
      showNotification('success', 'Exportação concluída com sucesso!');
      setShowModalExportar(false);
    } catch (err) {
      showNotification('danger', err.message || 'Erro ao exportar triagens.');
    } finally {
      setExporting(false);
    }
  };

  if (error) return <p>Erro: {error}</p>;

  return (
    <div className="content">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="font-weight-bolder mb-0">Triagens</h3>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleOpenModalExportar}>
            <i className="bi bi-download me-1"></i>Exportar
          </button>
          <Link to={'/Triagens/Novo'} className='btn btn-info btn-sm'>Nova Triagem</Link>
        </div>
      </div>
      <div className="card">
        <div className="card-body p-3">
          <div className="d-flex justify-content-end mb-3">
            <div className="input-group" style={{ maxWidth: '300px' }}>
              <span className="input-group-text border-0 bg-transparent">
                <i className="bi bi-search text-secondary"></i>
              </span>
              <input
                type="text"
                className="form-control border-start-0 ps-0"
                placeholder="Buscar por nome do paciente..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>

          <TriagensTable 
            triagens={triagens}
            loading={loading}
          />

          <PaginationControls currentCount={triagens.length} />
        </div>
      </div>

      <Modal show={showModalExportar} onHide={() => setShowModalExportar(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Exportar triagens</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleExportar}>
          <Modal.Body>
            <p className="text-muted small mb-3">Selecione o período para exportar as triagens.</p>
            <div className="row g-2">
              <div className="col-md-6">
                <label className="form-label">Data início</label>
                <DateInput
                  name="dataInicio"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                  className="form-control form-control-sm"
                  placeholder="dd/MM/yyyy"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Data fim</label>
                <DateInput
                  name="dataFim"
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                  className="form-control form-control-sm"
                  placeholder="dd/MM/yyyy"
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowModalExportar(false)}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={exporting}>
              {exporting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
                  Exportando...
                </>
              ) : (
                <>
                  <i className="bi bi-download me-1"></i>Exportar
                </>
              )}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Footer />
    </div>
  )
}

export default Triagens

